import { useState } from 'react';
import { ParticipanteSimulador } from '../../types/simulador';

interface ParticipantesSelectorProps {
  participantesSelecionados: ParticipanteSimulador[];
  onParticipantesChange: (participantes: ParticipanteSimulador[]) => void;
}

// Mock de participantes disponíveis
const PARTICIPANTES_MOCK: ParticipanteSimulador[] = [
  { id: '1', nome: 'João Silva', cpf: '123.456.789-00', email: 'joao@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: false },
  { id: '2', nome: 'Maria Santos', cpf: '234.567.890-11', email: 'maria@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: false },
  { id: '3', nome: 'Pedro Oliveira', cpf: '345.678.901-22', email: 'pedro@empresa.com', departamento: 'Vendas', cargo: 'Supervisor', selecionado: false },
  { id: '4', nome: 'Ana Costa', cpf: '456.789.012-33', email: 'ana@empresa.com', departamento: 'Comercial', cargo: 'Vendedor', selecionado: false },
  { id: '5', nome: 'Carlos Ferreira', cpf: '567.890.123-44', email: 'carlos@empresa.com', departamento: 'Comercial', cargo: 'Gerente', selecionado: false },
  { id: '6', nome: 'Juliana Lima', cpf: '678.901.234-55', email: 'juliana@empresa.com', departamento: 'Vendas', cargo: 'Vendedor', selecionado: false },
  { id: '7', nome: 'Roberto Alves', cpf: '789.012.345-66', email: 'roberto@empresa.com', departamento: 'Comercial', cargo: 'Vendedor', selecionado: false },
  { id: '8', nome: 'Fernanda Souza', cpf: '890.123.456-77', email: 'fernanda@empresa.com', departamento: 'Vendas', cargo: 'Coordenador', selecionado: false }
];

export const ParticipantesSelector = ({
  participantesSelecionados,
  onParticipantesChange
}: ParticipantesSelectorProps) => {
  const [filtro, setFiltro] = useState('');
  const [departamentoFiltro, setDepartamentoFiltro] = useState('');
  const [cargoFiltro, setCargoFiltro] = useState('');

  const participantesFiltrados = PARTICIPANTES_MOCK.filter(p => {
    const matchNome = p.nome.toLowerCase().includes(filtro.toLowerCase()) ||
                      p.cpf.includes(filtro) ||
                      p.email.toLowerCase().includes(filtro.toLowerCase());
    const matchDepartamento = !departamentoFiltro || p.departamento === departamentoFiltro;
    const matchCargo = !cargoFiltro || p.cargo === cargoFiltro;
    return matchNome && matchDepartamento && matchCargo;
  });

  const handleToggleParticipante = (id: string) => {
    const participante = PARTICIPANTES_MOCK.find(p => p.id === id);
    if (!participante) return;

    const jaSelecionado = participantesSelecionados.find(p => p.id === id);
    if (jaSelecionado) {
      onParticipantesChange(participantesSelecionados.filter(p => p.id !== id));
    } else {
      onParticipantesChange([...participantesSelecionados, { ...participante, selecionado: true }]);
    }
  };

  const handleSelecionarTodos = () => {
    onParticipantesChange(participantesFiltrados.map(p => ({ ...p, selecionado: true })));
  };

  const handleDeselecionarTodos = () => {
    onParticipantesChange([]);
  };

  const departamentos = Array.from(new Set(PARTICIPANTES_MOCK.map(p => p.departamento)));
  const cargos = Array.from(new Set(PARTICIPANTES_MOCK.map(p => p.cargo)));

  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Selecionar Participantes</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar por nome, CPF ou email..."
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
          
          <select
            value={departamentoFiltro}
            onChange={(e) => setDepartamentoFiltro(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Todos os departamentos</option>
            {departamentos.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          
          <select
            value={cargoFiltro}
            onChange={(e) => setCargoFiltro(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="">Todos os cargos</option>
            {cargos.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div className="flex justify-between items-center mb-3">
          <span className="text-sm text-gray-600">
            {participantesSelecionados.length} de {PARTICIPANTES_MOCK.length} selecionados
          </span>
          <div className="flex gap-2">
            <button
              onClick={handleSelecionarTodos}
              className="text-sm text-orange-600 hover:text-orange-700 font-medium whitespace-nowrap"
            >
              Selecionar todos
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={handleDeselecionarTodos}
              className="text-sm text-gray-600 hover:text-gray-700 font-medium whitespace-nowrap"
            >
              Limpar seleção
            </button>
          </div>
        </div>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase w-12">
                  <input
                    type="checkbox"
                    checked={participantesSelecionados.length === PARTICIPANTES_MOCK.length}
                    onChange={(e) => e.target.checked ? handleSelecionarTodos() : handleDeselecionarTodos()}
                    className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nome</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">CPF</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Departamento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cargo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {participantesFiltrados.map((participante) => {
                const selecionado = participantesSelecionados.find(p => p.id === participante.id);
                return (
                  <tr
                    key={participante.id}
                    className={`hover:bg-gray-50 cursor-pointer ${selecionado ? 'bg-orange-50' : ''}`}
                    onClick={() => handleToggleParticipante(participante.id)}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={!!selecionado}
                        onChange={() => {}}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{participante.nome}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{participante.cpf}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{participante.departamento}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{participante.cargo}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
